import * as React from "react";
import { Base, Kind, List } from "../../../schema/types/base";
import { homeLink, linksOf, request } from "../../../services/cpln";
import { FilterTerm, kindQuery } from "../../../schema/types/query/query";

export function useKindSelectData<T extends Base>(kind: Kind, queries: FilterTerm[] = []) {
  const [isLoading, setIsLoading] = React.useState(false);

  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [items, setItems] = React.useState<T[]>([]);
  const [nextLink, setNextLink] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const [queriedItems, setQueriedItems] = React.useState<T[]>([]);
  const [queriedNextLink, setQueriedNextLink] = React.useState<string | undefined>(undefined);
  const [queriedError, setQueriedError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  React.useEffect(() => {
    fetchInitialItems();
  }, [kind, JSON.stringify(queries)]);

  React.useEffect(() => {
    fetchInitialQueriedItems();
  }, [kind, JSON.stringify(queries), debouncedSearch]);

  async function fetchInitialItems() {
    try {
      setIsLoading(true);
      const method = "post";
      const url = homeLink(kind as any) + "/-query";
      const body: any = kindQuery(kind, queries);
      body.spec.sort = {
        by: "name",
        order: "asc",
      };
      const { data } = await request<List<T>>({ method: method, url: url, body: body });
      setItems(data.items);
      setNextLink(linksOf(data).next);
      setIsLoading(false);
      setError(undefined);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchPage() {
    if (!nextLink) {
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await request<List<T>>({ method: "get", url: nextLink });
      setItems((items) => {
        return [...items, ...data.items];
      });
      setNextLink(linksOf(data).next);
      setIsLoading(false);
      setError(undefined);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchInitialQueriedItems() {
    if (!debouncedSearch) {
      setQueriedNextLink(undefined);
      setQueriedItems([]);
      return;
    }

    try {
      setIsLoading(true);
      const method = "post";
      const url = homeLink(kind as any) + "/-query";
      const body: any = kindQuery(kind, queries, debouncedSearch);
      body.spec.sort = {
        by: "name",
        order: "asc",
      };
      const { data } = await request<List<T>>({ method: method, url: url, body: body });
      setQueriedItems(data.items);
      setQueriedNextLink(linksOf(data).next);
      setIsLoading(false);
      setQueriedError(undefined);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setQueriedError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchQueriedPage() {
    if (!queriedNextLink) {
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await request<List<T>>({ method: "get", url: queriedNextLink });
      setQueriedItems((items) => {
        return [...items, ...data.items];
      });
      setQueriedNextLink(linksOf(data).next);
      setIsLoading(false);
      setQueriedError(undefined);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setQueriedError(errorMessage);
      setIsLoading(false);
    }
  }

  const allItems = React.useMemo(() => {
    return [
      ...items.map((i) => ({ ...i, meta: { source: "default" } })),
      ...queriedItems.map((i) => ({ ...i, meta: { source: "query" } })),
    ];
  }, [items, queriedItems]);

  return {
    isLoading,
    setSearch,
    items,
    nextLink,
    fetchPage,
    error,
    queriedItems,
    queriedNextLink,
    fetchQueriedPage,
    queriedError,
    allItems,
  };
}
